import {useEffect, useState} from 'react';
import {
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Button,
} from '@chakra-ui/react';
import {
  ListType,
  useAddToMailingListMutation,
  useUserQuery,
} from '../../graphql/generated';
import {useAppToast} from '../../hooks/app-toast';
import {mixpanel} from '../../utils/mixpanel';
import {theme} from '../../style/theme';
import {useGeolocation} from '../../hooks/geolocation';
import {emailRegExp, sanitiseEmail} from '../../shared/consts';

const SubscribeSection = ({
  listType,
  buttonText,
  subscribedText,
  fbPixelAsset,
}: {
  listType: ListType;
  buttonText: string;
  subscribedText: string;
  fbPixelAsset?: string;
}): React.ReactElement => {
  const {countryCode: code} = useGeolocation();
  const {loading: userLoading, data: userData} = useUserQuery();
  const [email, setEmail] = useState<string>('');
  const [countryCode, setCountryCode] = useState<string>('');
  const [fbPixelEvent, setFbPixelEvent] = useState<boolean>(false);

  useEffect(() => {
    if (!userLoading && userData?.user?.email) {
      setEmail(userData?.user?.email);
    }
  }, [userLoading, userData]);

  useEffect(() => {
    if (fbPixelAsset && fbPixelEvent && email) {
      import('react-facebook-pixel')
        .then(({default: ReactPixel}) => {
          ReactPixel.init('1072919680201622');
          ReactPixel.trackCustom(fbPixelAsset, {
            content_name: email,
          });
        })
        // eslint-disable-next-line no-console
        .catch(console.warn);
    }
    return setFbPixelEvent(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fbPixelEvent]);

  const toast = useAppToast();

  const [addToMailingList, {loading}] = useAddToMailingListMutation({
    variables: {
      email: sanitiseEmail(email),
      countryCode,
      listType,
    },
    onCompleted: () => {
      toast.showInfoToast(subscribedText, 'Email sent!');
      setEmail('');
    },
    onError: () =>
      toast.showErrorToast('Something went wrong. Please try again.'),
  });

  const onSubmit = async () => {
    const validateEmail = emailRegExp.test(email);

    if (email === '')
      return toast.showErrorToast('Email address cannot be empty');
    if (!validateEmail)
      return toast.showErrorToast('Wrong format for the email address');

    mixpanel.track('Add To Waiting List', {
      email,
    });

    setCountryCode(code || '');

    if (fbPixelAsset) {
      setFbPixelEvent(true);
    }

    return addToMailingList();
  };
  return (
    <Flex
      w={{base: '100%'}}
      minW={{lg: '460px'}}
      flexDir="row"
      justifyContent="center"
      alignItems="center"
    >
      <InputGroup maxW="460px">
        <Input
          placeholder="Email Address"
          borderRadius="14px"
          bgColor="superlight"
          borderColor="superlight"
          _placeholder={{
            color: 'midGrey',
            fontFamily: 'Spectral',
            fontSize: {base: '15px', md: '18px', lg: '18px'},
          }}
          sx={{color: 'midGrey'}}
          onChange={(e) => setEmail(e.target.value)}
          value={email}
          height={50}
        />
        <InputRightElement w="50%" height={50} maxW="177px">
          <Button
            width="100%"
            bgColor={theme.colors.aqua['500']}
            onClick={onSubmit}
            isLoading={loading}
            _hover={{
              bgColor: theme.colors.aqua['500'],
            }}
            height="50px"
            textStyle="h7"
            color="white"
          >
            {buttonText}
          </Button>
        </InputRightElement>
      </InputGroup>
    </Flex>
  );
};

export {SubscribeSection};
